const EASING = "cubic-bezier(0.76, 0, 0.24, 1)";
const PIN_QUERY = "(min-width: 1024px)";
// A swipe has to travel this far, and be this much more horizontal than
// vertical, before it counts — anything else is the page being scrolled.
const SWIPE_DISTANCE = 40;
const SWIPE_RATIO = 1.2;
// Matches the copy's duration-200 fade: the outgoing block is given this long
// to clear out before the incoming one starts arriving.
const COPY_FADE = 200;

export default function textWithAssetVerticalSlider({
    count = 0,
    speed = 900,
} = {}) {
    return {
        count,
        speed,
        active: 0,
        // The copy lags the slide: it is hidden the moment the slide changes and
        // only the settled one is shown, so two blocks are never on screen at
        // once and slides scrolled straight past never flash their text.
        shown: 0,
        fadeTimer: null,
        clips: [],
        zIndexes: [],
        instant: [],
        topZIndex: 1,
        inView: false,
        pinned: false,
        // While a dot/key jump is scrolling, the slides it passes over must not
        // each take a turn on screen, so scroll sync is parked on the target.
        pendingTarget: null,
        releaseTimer: null,
        syncScroll: null,
        touchStart: null,

        init() {
            this.clips = Array.from({ length: this.count }, () => "inset(0)");
            this.zIndexes = Array.from({ length: this.count }, (_, i) =>
                i === 0 ? 1 : 0,
            );
            this.instant = Array.from({ length: this.count }, () => false);

            // Arrow keys only act while the block is actually on screen, so
            // they never steal the keyboard from the rest of the page.
            const observer = new IntersectionObserver(
                ([entry]) => {
                    this.inView = entry.isIntersecting;
                },
                { threshold: 0.5 },
            );
            observer.observe(this.$refs.panel);

            this.initScrollDrive();
            this.initSwipe();
        },

        // Unpinned the block is a plain stack, so scroll can't walk it: a
        // sideways swipe steps through the slides instead. Vertical movement is
        // left alone so the page still scrolls normally through the block.
        initSwipe() {
            if (this.count < 2) return;

            const panel = this.$refs.panel;

            panel.addEventListener(
                "touchstart",
                (event) => {
                    if (this.pinned || event.touches.length !== 1) {
                        this.touchStart = null;
                        return;
                    }

                    const touch = event.touches[0];
                    this.touchStart = { x: touch.clientX, y: touch.clientY };
                },
                { passive: true },
            );

            // A second finger means a pinch, not a swipe.
            panel.addEventListener(
                "touchmove",
                (event) => {
                    if (event.touches.length > 1) this.touchStart = null;
                },
                { passive: true },
            );

            panel.addEventListener(
                "touchend",
                (event) => {
                    const start = this.touchStart;
                    this.touchStart = null;
                    if (!start || this.pinned) return;

                    const touch = event.changedTouches[0];
                    if (!touch) return;

                    const dx = touch.clientX - start.x;
                    const dy = touch.clientY - start.y;
                    if (Math.abs(dx) < SWIPE_DISTANCE) return;
                    if (Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) return;

                    // Clamped, not wrapped, so the dots stay an honest map of
                    // where the swipe can still go.
                    const target = this.active + (dx < 0 ? 1 : -1);
                    if (target < 0 || target > this.count - 1) return;

                    this.select(target);
                },
                { passive: true },
            );
        },

        // The block is taller than the viewport and its inner panel is sticky,
        // so page scroll walks through the slides before the page moves on.
        initScrollDrive() {
            if (this.count < 2) return;

            const media = window.matchMedia(PIN_QUERY);
            let frame = null;

            const sync = () => {
                frame = null;
                if (!this.pinned) return;

                const rect = this.$el.getBoundingClientRect();
                const travel = rect.height - window.innerHeight;
                if (travel <= 0) return;

                const progress = Math.min(
                    Math.max(-rect.top / travel, 0),
                    0.9999,
                );
                const index = Math.floor(progress * this.count);

                if (this.pendingTarget !== null) {
                    if (index === this.pendingTarget) this.release();
                    return;
                }

                this.goTo(index);
            };

            const schedule = () => {
                if (frame === null) frame = requestAnimationFrame(sync);
            };

            const setMode = () => {
                this.pinned = media.matches;
                schedule();
            };

            this.syncScroll = schedule;

            // A manual scroll cancels the jump, so an interrupted smooth
            // scroll can never leave sync parked on a target it won't reach.
            const release = () => this.release();

            media.addEventListener("change", setMode);
            window.addEventListener("wheel", release, { passive: true });
            window.addEventListener("touchstart", release, { passive: true });
            window.addEventListener("scroll", schedule, { passive: true });
            window.addEventListener("resize", schedule, { passive: true });
            setMode();
        },

        // Scroll position owns the active slide while pinned, so dots and keys
        // move the page instead of fighting it.
        select(index) {
            if (this.count < 2) return;

            if (!this.pinned) {
                this.goTo(index);
                return;
            }

            const target = Math.min(Math.max(index, 0), this.count - 1);
            const rect = this.$el.getBoundingClientRect();
            const travel = rect.height - window.innerHeight;
            if (travel <= 0) {
                this.goTo(target);
                return;
            }

            const top = rect.top + window.scrollY;
            // Land mid-segment so rounding never leaves us on a neighbour.
            const offset = ((target + 0.5) / this.count) * travel;
            this.jumpTo(target, top + offset);
        },

        // The jump animates the slide once up front and parks scroll sync until
        // the page arrives, so the slides in between never flash past.
        jumpTo(target, y) {
            this.pendingTarget = target;
            this.goTo(target);

            // However the scroll animation ends, sync is never parked longer
            // than the jump could plausibly take.
            clearTimeout(this.releaseTimer);
            this.releaseTimer = setTimeout(() => this.release(), 2000);

            // Lenis owns page scroll here, so ask it directly when it is around
            // and fall back to the native smooth scroll when it isn't.
            if (window.lenis) {
                window.lenis.scrollTo(y, { onComplete: () => this.release() });
                return;
            }

            window.scrollTo({ top: y, behavior: "smooth" });
        },

        // Hands the active slide back to scroll position.
        release() {
            clearTimeout(this.releaseTimer);
            this.releaseTimer = null;
            if (this.pendingTarget === null) return;

            this.pendingTarget = null;
            this.syncScroll?.();
        },

        goTo(index) {
            if (this.count < 2) return;

            const target = ((index % this.count) + this.count) % this.count;
            if (target === this.active) return;

            // The incoming slide is wiped in over the outgoing one: upwards
            // when moving forward, downwards when moving back. The outgoing
            // slide is left untouched underneath it.
            const forward = index > this.active;
            this.clips[target] = forward
                ? "inset(100% 0 0 0)"
                : "inset(0 0 100% 0)";
            this.instant[target] = true;
            this.zIndexes[target] = ++this.topZIndex;
            this.active = target;

            this.shown = -1;
            clearTimeout(this.fadeTimer);
            this.fadeTimer = setTimeout(() => {
                this.shown = this.active;
            }, COPY_FADE);

            // Two frames: one for the collapsed start state to paint without a
            // transition, one to animate out of it.
            requestAnimationFrame(() =>
                requestAnimationFrame(() => {
                    this.instant[target] = false;
                    this.clips[target] = "inset(0)";
                }),
            );
        },

        next() {
            this.select(this.active + 1);
        },

        prev() {
            this.select(this.active - 1);
        },

        slideStyle(index) {
            return {
                clipPath: this.clips[index],
                zIndex: this.zIndexes[index],
                transition: this.instant[index]
                    ? "none"
                    : `clip-path ${this.speed}ms ${EASING}`,
            };
        },

        onKey(event) {
            if (this.count < 2 || !this.inView) return;

            const { key, target } = event;
            const vertical = key === "ArrowUp" || key === "ArrowDown";
            const horizontal = key === "ArrowLeft" || key === "ArrowRight";
            if (!vertical && !horizontal) return;

            if (
                target instanceof Element &&
                target.closest("input, textarea, select, [contenteditable]")
            ) {
                return;
            }

            // Up/Down are the page's scroll keys, so only claim them once the
            // slider itself holds focus. Left/Right are always safe here.
            if (vertical && !this.$el.contains(document.activeElement)) return;

            event.preventDefault();
            key === "ArrowUp" || key === "ArrowLeft" ? this.prev() : this.next();
        },
    };
}
